import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { cStories } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import UserAvatar from './UserAvatar';
import StoryComposer from './StoryComposer';
import StoryViewer from './StoryViewer';
import { BLUE } from './brand';

/**
 * Facebook-style story bar on the first page: "Create story" + one circle per
 * member (blue ring while there is something unseen, grey afterwards).
 * Stories live 24 hours — see `POST /api/community/stories`.
 */
export default function StoriesBar() {
  const { t } = useLanguage();
  const { user, openAuth } = useCommunity();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(-1);

  const load = useCallback(() => {
    return cStories()
      .then((rows) => setGroups(Array.isArray(rows) ? rows : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const mine = user ? groups.find((g) => g.user_id === user.id) : null;
  const others = groups.filter((g) => !user || g.user_id !== user.id);

  const create = () => {
    if (!user) { openAuth('signin', t('loginRequiredDesc')); return; }
    setCreating(true);
  };

  const openStory = (group) => {
    if (!user) { openAuth('signin', t('loginRequiredDesc')); return; }
    const index = groups.findIndex((g) => g.user_id === group.user_id);
    setViewing(index < 0 ? 0 : index);
  };

  const Circle = ({ group, label, addBadge }) => (
    <button
      type="button"
      onClick={() => openStory(group)}
      className="flex flex-col items-center gap-1.5 w-[74px] shrink-0 group"
      title={group.author?.name || group.author?.username}
    >
      <span
        className={`w-[68px] h-[68px] rounded-full p-[3px] ${group.unseen ? 'story-ring' : 'story-ring-seen'}`}
      >
        <span className="relative block w-full h-full rounded-full p-[2px] bg-white dark:bg-gray-900">
          <UserAvatar user={group.author} size={58} />
          {addBadge && (
            <span
              className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center text-white"
              style={{ background: BLUE }}
            >
              <FiPlus className="w-3.5 h-3.5" />
            </span>
          )}
        </span>
      </span>
      <span className="text-[11px] font-semibold truncate w-full text-center dark:text-gray-200">
        {label}
      </span>
    </button>
  );

  return (
    <section className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-3">
      <div className="flex items-center justify-between mb-2 px-1">
        <h2 className="font-extrabold text-sm dark:text-gray-100">{t('stories')}</h2>
        <span className="text-[11px] text-gray-400">{t('storiesHint')}</span>
      </div>

      <div className="flex items-stretch gap-3 overflow-x-auto no-scrollbar pb-1">
        {/* Create story */}
        <button
          type="button"
          onClick={create}
          className="relative w-[74px] shrink-0 flex flex-col items-center gap-1.5"
        >
          <span className="story-ring w-[68px] h-[68px] rounded-full p-[3px]">
            <span className="relative block w-full h-full rounded-full p-[2px] bg-white dark:bg-gray-900">
              {user ? (
                <UserAvatar user={user} size={58} />
              ) : (
                <span className="w-full h-full rounded-full flex items-center justify-center text-white" style={{ background: BLUE }}>
                  <FiPlus className="w-7 h-7" />
                </span>
              )}
              <span
                className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center text-white"
                style={{ background: BLUE }}
              >
                <FiPlus className="w-3.5 h-3.5" />
              </span>
            </span>
          </span>
          <span className="text-[11px] font-semibold truncate w-full text-center" style={{ color: BLUE }}>
            {user && mine ? t('yourStory') : t('createStory')}
          </span>
        </button>

        {/* My own story (when it exists) */}
        {mine && <Circle group={mine} label={mine.author?.name || t('yourStory')} addBadge />}

        {others.map((g) => (
          <Circle key={g.user_id} group={g} label={g.author?.name || g.author?.username || '—'} />
        ))}

        {!loading && groups.length === 0 && (
          <p className="text-xs text-gray-400 self-center pl-1">{t('noStories')}</p>
        )}
      </div>

      {creating && (
        <StoryComposer
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}

      {viewing >= 0 && groups[viewing] && (
        <StoryViewer
          groups={groups}
          startIndex={viewing}
          onClose={() => { setViewing(-1); load(); }}
          onDelete={(id) => {
            setGroups((prev) => prev
              .map((g) => ({ ...g, stories: g.stories.filter((s) => s.id !== id) }))
              .filter((g) => g.stories.length > 0));
          }}
          onCreate={() => { setViewing(-1); load(); create(); }}
        />
      )}
    </section>
  );
}
